/**
 * ExportMenu — botão único "Exportar" que abre um dropdown com os formatos
 * (PDF / Excel / CSV). Substitui o padrão de 3 botões soltos lado a lado.
 *
 * Uso:
 *   <ExportMenu onExport={(formato) => exportarDre(formato, dados)}
 *               disabled={!temDados} />
 *
 * `onExport` recebe o id do formato ("pdf" | "excel" | "csv"; com `comImagem`,
 * também "png" | "jpg" — a tela decide o que capturar). `somente` restringe
 * a lista (ex.: ["pdf", "png", "jpg"] nas folhas de apontamento, que não têm
 * tabela para Excel/CSV). Auto-contido:
 * fecha ao clicar fora ou apertar Esc. Estilo via tokens (sem CSS canônico
 * novo) pra poder ser usado em qualquer módulo.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown, FiDownload } from "react-icons/fi";

const FORMATOS = [
  { id: "pdf", label: "PDF" },
  { id: "excel", label: "Excel (.xlsx)" },
  { id: "csv", label: "CSV" },
];
const FORMATOS_IMAGEM = [
  { id: "png", label: "Imagem (PNG)" },
  { id: "jpg", label: "Imagem (JPG)" },
];

export default function ExportMenu({ onExport, disabled = false, label = "Exportar", comImagem = false, somente = null }) {
  const todos = comImagem ? [...FORMATOS, ...FORMATOS_IMAGEM] : FORMATOS;
  const formatos = somente ? todos.filter((f) => somente.includes(f.id)) : todos;
  const [aberto, setAberto] = useState(false);
  const [hover, setHover] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const onClickFora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setAberto(false); };
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onEsc);
    };
  }, [aberto]);

  const escolher = (formato) => {
    setAberto(false);
    onExport?.(formato);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" className="btn btn--ghost" disabled={disabled}
              aria-haspopup="menu" aria-expanded={aberto}
              onClick={() => setAberto((a) => !a)}>
        <FiDownload size={14} aria-hidden="true" /> {label}
        <FiChevronDown size={13} aria-hidden="true"
                       style={{ opacity: 0.6, transition: "transform .15s ease", transform: aberto ? "rotate(180deg)" : "none" }} />
      </button>
      {aberto && (
        <div role="menu" style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 60,
          minWidth: 168, padding: 4,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-2)",
        }}>
          {formatos.map((f) => (
            <button key={f.id} type="button" role="menuitem"
                    onClick={() => escolher(f.id)}
                    onMouseEnter={() => setHover(f.id)} onMouseLeave={() => setHover(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, width: "100%",
                      padding: "8px 12px", border: 0, cursor: "pointer", textAlign: "left",
                      fontSize: 13, color: "var(--ink)", borderRadius: "var(--radius-sm)",
                      background: hover === f.id ? "var(--surface-2)" : "transparent",
                    }}>
              <FiDownload size={13} aria-hidden="true" style={{ opacity: 0.6 }} /> {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
